import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Tabs,
  Tab,
  Divider,
  Alert,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Tooltip,
  MenuItem,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { 
  CalendarMonth as CalendarIcon,
  Event as EventIcon,
  EventAvailable as ConfirmIcon,
  EventBusy as RejectIcon,
  Visibility as ViewIcon,
  PersonAdd as PersonAddIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  VpnKey as PremiumIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { format, addMonths, startOfDay, isEqual, isAfter, isBefore, addDays, getHours } from 'date-fns';
import { nl } from 'date-fns/locale';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, Timestamp, doc, updateDoc, where, writeBatch } from 'firebase/firestore';
import UserSubscriptionManager, { SUBSCRIPTION_TIERS, TIER_CONFIG } from '../components/UserSubscriptionManager';
import { useLanguage } from '../contexts/LanguageContext';

// Interface for Firestore appointment document data
interface Appointment {
  id: string; // Firestore Document ID
  userId: string;
  userEmail: string;
  userName: string; // Added userName
  appointmentDate?: Timestamp; 
  appointmentTime?: string; 
  appointmentType: string;
  notes?: string;
  status: 'pending' | 'pending_user_request' | 'confirmed' | 'rejected'; 
  requestedAt: Timestamp;
}

// Interface for blocked time slots
interface BlockedTimeSlot {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  reason: string;
}

// Define Tier type alias locally (matching AuthContext)
type Tier = 'Self' | 'Premium' | 'Complete' | 'None';

// Updated User interface to align with Firestore structure
interface User {
  id: string; // Firebase UID
  email: string;
  name: string; // Should be fullName in Firestore?
  subscriptionTier?: string;
  subscriptionExpiry?: string;
  tier: Tier;
  premiumExpiresAt: string | null;
  registeredAt: string;
  lastLogin: string;
  role?: 'user' | 'coach' | 'admin';
  nextAppointmentDate?: string | null;
}

// Available time slots
const availableTimeSlots = [
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`coach-tabpanel-${index}`}
      aria-labelledby={`coach-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `coach-tab-${index}`,
    'aria-controls': `coach-tabpanel-${index}`,
  };
}

// Helper function to safely format Timestamp or return placeholder
const formatFirestoreTimestamp = (timestamp: any, dateFormat = 'dd/MM/yyyy HH:mm'): string => {
  if (timestamp instanceof Timestamp) {
    try {
      return format(timestamp.toDate(), dateFormat, { locale: nl });
    } catch (e) {
      console.error("Error formatting timestamp:", e);
      return 'Ongeldige datum';
    }
  }
  // Handle cases where it might be a string or null/undefined already
  if (typeof timestamp === 'string') {
     return timestamp; // Assume already formatted if string
  }
  return '-'; // Placeholder for null, undefined, or other types
};

const CoachDashboard = () => {
  const { currentUser, updateUserPremium, isCoach, loading: authLoading, refreshUserSubscription } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [blockDialog, setBlockDialog] = useState(false);
  const [viewAppointmentDialog, setViewAppointmentDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  
  // Add message state for notifications
  const [message, setMessage] = useState<{text: string, type: 'success' | 'error' | 'info' | 'warning'} | null>(null);
  
  // Add the helper function to get tier name by key - now inside the component with access to the language variable
  const getTierNameByKey = (tierKey: string): string => {
    // Handle both old and new tier formats
    switch(tierKey.toLowerCase()) {
      case SUBSCRIPTION_TIERS.PLATINUM:
        return language === 'nl' ? 'Platinum' : 'Platinum';
      case SUBSCRIPTION_TIERS.ELITE:
        return language === 'nl' ? 'Elite' : 'Elite';
      case SUBSCRIPTION_TIERS.GOLD:
        return language === 'nl' ? 'Gold' : 'Gold';
      case SUBSCRIPTION_TIERS.ESSENTIAL:
        return language === 'nl' ? 'Essential' : 'Essential';
      case SUBSCRIPTION_TIERS.NONE:
        return language === 'nl' ? 'Geen' : 'None';
      case 'premium':
        return language === 'nl' ? 'Premium' : 'Premium';
      case 'complete':
        return language === 'nl' ? 'Complete' : 'Complete';
      case 'self':
        return language === 'nl' ? 'Self-study' : 'Self-study';
      default:
        return tierKey;
    }
  };
  
  // State for data - Initialize as empty arrays
  const [blockedTimeSlots, setBlockedTimeSlots] = useState<BlockedTimeSlot[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  
  // State for users fetched from Firestore
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  
  // Separate state for appointments
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [appointmentsError, setAppointmentsError] = useState<string | null>(null);
  
  // State for dialogs and forms
  const [userDialog, setUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [premiumForm, setPremiumForm] = useState({
    tier: 'None' as Tier,
    expiresAt: format(addMonths(new Date(), 12), 'yyyy-MM-dd')
  });
  
  // Updated blockForm state for date range
  const [blockForm, setBlockForm] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'), // Add end date
    reason: ''
  });

  // State for New Appointment Dialog
  const [newAppointmentDialog, setNewAppointmentDialog] = useState(false);
  const [newAppointmentForm, setNewAppointmentForm] = useState({
    clientId: '', // User ID
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '14:00', // Default start time
    duration: '60', // Default duration in minutes (e.g., 60 for 1 hour)
    type: 'Vervolggesprek', // Default type
    notes: ''
  });
  const [newAppointmentError, setNewAppointmentError] = useState('');
  const [newAppointmentLoading, setNewAppointmentLoading] = useState(false);
  
  // --- NEW State for Scheduling Dialog ---
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [schedulingAppointment, setSchedulingAppointment] = useState<Appointment | null>(null);
  const [scheduleForm, setScheduleForm] = useState({ date: '', time: '' });
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleLoading, setScheduleLoading] = useState(false);
  // --- END NEW State ---
  
  // --- Permission Check ---
  useEffect(() => {
    // Wait for auth loading to finish
    if (!authLoading) {
      if (!isCoach()) {
        console.warn("Access Denied: User is not a coach. Redirecting...");
        // Redirect non-coach users (e.g., to home or login)
        navigate('/');
      }
    }
  }, [isCoach, authLoading, navigate]);
  
  // --- Data Fetching ---
  const fetchAndProcessUsers = async (upcomingAppointments: Appointment[]) => {
    if (!isCoach()) return;
    
    console.log("[CoachDashboard] Starting fetchUsers..."); 
    setUsersLoading(true);
    setUsersError(null);
    let fetchedUsers: User[] = []; // Define outside try
    try {
      const usersColRef = collection(db, 'SAW_users');
      const q = query(usersColRef, orderBy("email")); 
      console.log("[CoachDashboard] Executing users query..."); 
      const querySnapshot = await getDocs(q);
      console.log(`[CoachDashboard] Users query returned ${querySnapshot.size} documents.`); 
      
      if (querySnapshot.empty) {
         console.warn("[CoachDashboard] No documents found in SAW_users collection.");
      }

      // Map initial user data
      fetchedUsers = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`[CoachDashboard] Processing user doc ID: ${doc.id}, Data:`, data); 
        const subscriptionData = data.subscription as { tier?: Tier, status?: string, premium_end_date?: Timestamp } | undefined;
        const currentTier: Tier = subscriptionData?.tier || 'None';
        const premiumEndDateTimestamp = subscriptionData?.premium_end_date;
        let premiumExpiresAtString: string | null = null;
        if (premiumEndDateTimestamp instanceof Timestamp && (currentTier === 'Premium' || currentTier === 'Complete')) {
            premiumExpiresAtString = formatFirestoreTimestamp(premiumEndDateTimestamp, 'dd/MM/yyyy'); // Only format expiry if relevant tier
        }
        
        // Map basic user data FIRST
        const basicUserData: User = {
          id: doc.id, 
          email: data.email || '',
          name: data.fullName || data.email || 'Unknown User',
          tier: data.subscription?.tier || 'None', // Support old tier format
          premiumExpiresAt: data.subscription?.premium_end_date 
            ? formatFirestoreTimestamp(data.subscription.premium_end_date, 'dd/MM/yyyy') 
            : null,
          registeredAt: formatFirestoreTimestamp(data.createdAt),
          lastLogin: formatFirestoreTimestamp(data.lastLogin),
          role: data.role || 'user',
          nextAppointmentDate: null,
          // Add new subscription fields
          subscriptionTier: data.subscriptionTier || data.subscription?.tier || 'geen', // Try new field first, fall back to old
          subscriptionExpiry: data.subscriptionExpiry 
            ? data.subscriptionExpiry 
            : data.subscription?.premium_end_date 
              ? formatFirestoreTimestamp(data.subscription.premium_end_date, 'yyyy-MM-dd') 
              : null
        };
        console.log(`[CoachDashboard] Basic mapped user data for ${doc.id}:`, basicUserData); 
        return basicUserData;
      });

    } catch (error) {
      console.error("[CoachDashboard] Error fetching users: ", error); 
      setUsersError("Kon gebruikers niet laden. Probeer het later opnieuw.");
      setUsersLoading(false); // Set loading false on error
      return; // Exit if user fetch fails
    } 
    // No finally here, set loading false after processing below

    // --- Correlate with Appointments --- 
    console.log("[CoachDashboard] Correlating users with upcoming appointments...");
    const usersWithAppointments = fetchedUsers.map(user => {
       // Find the first upcoming confirmed appointment for this user
      const nextAppt = upcomingAppointments.find(appt => appt.userId === user.id);
      
      return {
         ...user,
         nextAppointmentDate: nextAppt ? formatFirestoreTimestamp(nextAppt.appointmentDate, 'dd/MM/yyyy') : null
      };
    });
    
    console.log("[CoachDashboard] Setting final users state:", usersWithAppointments);
    setUsers(usersWithAppointments);
    setUsersLoading(false); // Set loading false after all processing
    console.log("[CoachDashboard] fetchAndProcessUsers finished.");
  };

  const fetchAppointments = async (): Promise<Appointment[]> => { // Return appointments
    if (!isCoach()) return [];
    setAppointmentsLoading(true);
    setAppointmentsError(null);
    let fetchedAppointments: Appointment[] = []; // Define outside try
    try {
      const appointmentsColRef = collection(db, 'appointments');
      const todayTimestamp = Timestamp.fromDate(startOfDay(new Date()));
      const q = query(
        appointmentsColRef,
        where("status", "in", ["confirmed", "pending", "pending_user_request"]),
        orderBy("requestedAt", "desc")
      );
      console.log("[CoachDashboard] Executing upcoming appointments query (all relevant statuses)...");
      const querySnapshot = await getDocs(q);
      console.log(`[CoachDashboard] Upcoming appointments query returned ${querySnapshot.size} documents.`);
      
      fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as Omit<Appointment, 'id'>)
      }));
      // Don't set state here, return the data
      // setAppointments(fetchedAppointments); 
      console.log("[CoachDashboard] Fetched upcoming appointments:", fetchedAppointments);
    } catch (error) {
      console.error("[CoachDashboard] Error fetching appointments: ", error);
      setAppointmentsError("Kon afspraken niet laden.");
    } finally {
      setAppointmentsLoading(false);
    }
    return fetchedAppointments; // Return the fetched data or empty array on error
  };

  // Fetch users and appointments sequentially
  useEffect(() => {
    const loadData = async () => {
      if (!authLoading && isCoach()) {
        const upcomingAppointments = await fetchAppointments(); // Fetch appointments first
        await fetchAndProcessUsers(upcomingAppointments); // Then fetch users and process
        // TODO: Fetch blocked times here as well
      }
    };
    loadData();
  }, [authLoading, isCoach]); // Rerun when auth state or coach status changes

  // --- Event Handlers (Appointments/Blocking - Keep structure for now) ---
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleOpenBlockDialog = () => {
    // Reset form to default values using correct state shape
    setBlockForm({
      startDate: format(selectedDate || new Date(), 'yyyy-MM-dd'),
      endDate: format(selectedDate || new Date(), 'yyyy-MM-dd'),
      reason: ''
    });
    setBlockDialog(true);
  };

  const handleCloseBlockDialog = () => {
    setBlockDialog(false);
  };

  const handleOpenUserDialog = (user: User) => {
    setSelectedUser(user);
    // Populate form with selected user's current tier and expiry
    setPremiumForm({
      tier: user.tier,
      expiresAt: user.premiumExpiresAt 
                  ? format(new Date(user.premiumExpiresAt.split('/').reverse().join('-')), 'yyyy-MM-dd')
                  : format(addMonths(new Date(), 12), 'yyyy-MM-dd') 
    });
    setUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setUserDialog(false);
    setSelectedUser(null);
    // Reset form if needed
  };

  const handleViewAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setViewAppointmentDialog(true);
  };

  const handleCloseViewAppointmentDialog = () => {
    setViewAppointmentDialog(false);
    setSelectedAppointment(null);
  };

  // Updated to handle single day or range (DEMO)
  const handleAddBlockedTimeSlot = async (type: 'single' | 'range') => {
    setUsersLoading(true); // Use loading state
    try {
      const startDate = new Date(blockForm.startDate + 'T00:00:00'); // Ensure parsing in local timezone
      const endDate = new Date(blockForm.endDate + 'T00:00:00');

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        alert('Ongeldige start- of einddatum.');
        return;
      }

      if (isAfter(startDate, endDate)) {
        alert('Startdatum kan niet na de einddatum liggen.');
        return;
      }

      // --- Firestore Logic Placeholder (Option A: One doc per day) ---
      console.log('Preparing to block date range:', blockForm);
      const datesToBlock = [];
      let currentDate = startOfDay(startDate); // Use startOfDay for comparison
      const finalEndDate = startOfDay(endDate);

      while (isBefore(currentDate, finalEndDate) || isEqual(currentDate, finalEndDate)) {
        datesToBlock.push(format(currentDate, 'yyyy-MM-dd'));
        currentDate = addDays(currentDate, 1);
      }

      console.log('Dates to block:', datesToBlock);
      console.log('Reason:', blockForm.reason);
      
      // ** ACTUAL FIRESTORE IMPLEMENTATION NEEDED HERE **
      // Example using writeBatch:
      // const batch = writeBatch(db);
      // const blockedDaysRef = collection(db, 'blockedDays'); // Or your collection name
      // datesToBlock.forEach(dateString => {
      //   const docRef = doc(blockedDaysRef, dateString); // Use YYYY-MM-DD as doc ID
      //   batch.set(docRef, { reason: blockForm.reason, blockedAt: serverTimestamp() });
      // });
      // await batch.commit();
      // console.log('Firestore batch commit simulated.');
      // ** END OF FIRESTORE PLACEHOLDER **

      // Update local state (for immediate UI feedback - adjust if using real fetch)
      const newBlockedSlots: BlockedTimeSlot[] = datesToBlock.map(dateStr => ({
         id: `${dateStr}-${Date.now()}`, // Temp ID
         date: dateStr,
         startTime: '00:00', // Indicate whole day
         endTime: '23:59', // Indicate whole day
         reason: blockForm.reason || 'Hele dag geblokkeerd'
       }));
       // Assuming blockedTimeSlots state now holds objects representing whole days
      setBlockedTimeSlots(prev => [...prev, ...newBlockedSlots]); 

      handleCloseBlockDialog();
      alert('Datumbereik geblokkeerd (DEMO - check console voor details).');

    } catch (error) {
      console.error("Error blocking date range:", error);
      alert('Fout bij het blokkeren van datumbereik.');
    } finally {
       setUsersLoading(false);
    }
  };

  // Updated to handle deletion of range-blocked days (DEMO)
  const handleRemoveBlockedTimeSlot = async (dateString: string) => { // Assuming ID is now date string
    setUsersLoading(true);
    try {
       // ** ACTUAL FIRESTORE IMPLEMENTATION NEEDED HERE **
       // Example using deleteDoc:
       // const docRef = doc(db, 'blockedDays', dateString);
       // await deleteDoc(docRef);
       // console.log('Firestore delete simulated for:', dateString);
       // ** END OF FIRESTORE PLACEHOLDER **

      // Update local state 
      setBlockedTimeSlots(prev => prev.filter(slot => slot.date !== dateString));
      alert('Blokkade voor dag verwijderd (DEMO - check console).');
    } catch (error) {
       console.error("Error removing blocked day:", error);
       alert('Fout bij het verwijderen van blokkade.');
    } finally {
       setUsersLoading(false);
    }
  };

  const handleUpdateAppointmentStatus = async (appointmentId: string, newStatus: 'confirmed' | 'rejected') => {
    setUsersLoading(true);
    try {
      const appointmentRef = doc(db, "appointments", appointmentId);
      await updateDoc(appointmentRef, { status: newStatus });
      // Refresh local state
      setAppointments(prev => 
        prev.map(app => app.id === appointmentId ? { ...app, status: newStatus } : app)
      );
      // Optionally close view dialog if open
      if (selectedAppointment?.id === appointmentId) {
        handleCloseViewAppointmentDialog();
      }
      // TODO: Send confirmation/rejection email to user
      alert(`Afspraak ${newStatus === 'confirmed' ? 'bevestigd' : 'afgewezen'}.`);
    } catch (error) {
      console.error("Error updating appointment status: ", error);
      alert("Kon status niet bijwerken. Probeer het opnieuw.");
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle updating user's premium status via the dialog
  const handleUpdateUserPremium = async () => {
    if (!selectedUser) return;

    setUsersLoading(true); // Indicate loading state
    let expiryDate: Date | undefined = undefined;

    // Parse expiry date ONLY if tier is Premium or Complete
    if (premiumForm.tier === 'Premium' || premiumForm.tier === 'Complete') {
        if (premiumForm.expiresAt) {
            try {
                // Ensure the date string is valid before creating a Date object
                expiryDate = new Date(premiumForm.expiresAt);
                if (isNaN(expiryDate.getTime())) {
                    console.error('Invalid Date provided for expiry:', premiumForm.expiresAt);
                    setUsersError('Ongeldige vervaldatum opgegeven.'); // Set error message
                    setUsersLoading(false);
                    return; // Stop execution if date is invalid
                }
            } catch (e) {
                console.error('Error parsing expiry date:', e);
                setUsersError('Fout bij verwerken vervaldatum.');
                setUsersLoading(false);
                return;
            }
        } else {
             console.warn('Expiry date not set for Premium/Complete tier.');
             // Decide if expiry is mandatory for these tiers
             // setUsersError('Vervaldatum is verplicht voor Premium/Complete.');
             // setUsersLoading(false);
             // return;
             // Or allow setting without expiry?
        }
    } // If tier is None or Self, expiryDate remains undefined

    console.log(`Attempting to update user ${selectedUser.id} to tier ${premiumForm.tier} with expiry ${expiryDate}`);

    try {
      // Call the updated updateUserPremium from AuthContext
      const success = await updateUserPremium(
        selectedUser.id, // Pass the Firebase UID
        premiumForm.tier, // Pass the selected Tier
        expiryDate // Pass the Date object or undefined
      );

      if (success) {
        console.log(`Successfully updated tier for user ${selectedUser.id}`);
        // Update the user list locally for immediate UI feedback
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === selectedUser.id 
              ? { 
                  ...user, 
                  tier: premiumForm.tier, 
                  premiumExpiresAt: expiryDate ? format(expiryDate, 'dd/MM/yyyy') : null, // Update local expiry string
                  subscriptionTier: premiumForm.tier,
                  subscriptionExpiry: expiryDate ? format(expiryDate, 'yyyy-MM-dd') : undefined
                } 
              : user
          )
        );
        handleCloseUserDialog(); // Close dialog on success
        // Also refresh the user's subscription to ensure current state is displayed
        if (currentUser && currentUser.uid === selectedUser.id) {
          refreshUserSubscription();
        }
        setMessage({
          text: language === 'nl' ? 'Gebruiker bijgewerkt' : 'User updated successfully',
          type: 'success'
        });
      } else {
        console.error(`Failed to update tier for user ${selectedUser.id}`);
        setUsersError('Opslaan van abonnementsstatus mislukt.'); // Set error message
      }
    } catch (error) {
        console.error('Error calling updateUserPremium:', error);
        setUsersError('Er is een onverwachte fout opgetreden.'); // Set generic error message
    }

    setUsersLoading(false); // Reset loading state
  };

  // --- Helper Functions (Appointments/Blocking - Keep for now) ---
  const isTimeSlotBlocked = (date: string, time: string): boolean => {
    // Basic check against mock data for now
    return blockedTimeSlots.some(slot => 
      slot.date === date &&
      time >= slot.startTime &&
      time < slot.endTime // Assuming end time is exclusive
    );
  };

  const getAppointmentsForDate = (date: string): Appointment[] => {
    // Basic filter against mock data for now
    return appointments.filter(req => formatFirestoreTimestamp(req.appointmentDate, 'yyyy-MM-dd') === date);
  };
  
  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'confirmed':
        return 'Bevestigd';
      case 'rejected':
        return 'Afgewezen';
      case 'pending':
        return 'In afwachting';
      default:
        return status;
    }
  };

  // Handlers for New Appointment Dialog
  const handleOpenNewAppointmentDialog = () => {
    setNewAppointmentForm({ // Reset form on open
      clientId: '',
      date: format(selectedDate || new Date(), 'yyyy-MM-dd'),
      time: '14:00',
      duration: '60',
      type: 'Vervolggesprek',
      notes: ''
    });
    setNewAppointmentError('');
    setNewAppointmentDialog(true);
  };

  const handleCloseNewAppointmentDialog = () => {
    setNewAppointmentDialog(false);
    setNewAppointmentError(''); // Clear error on close
  };

  // Adjusted submit handler - doesn't need event parameter when called from button
  const handleNewAppointmentSubmit = async () => {
    // if (e) e.preventDefault(); // Removed - No longer needed from button click
    setNewAppointmentError('');
    
    // Basic Validation
    if (!newAppointmentForm.clientId || !newAppointmentForm.date || !newAppointmentForm.time || !newAppointmentForm.duration) {
      setNewAppointmentError('Selecteer a.u.b. een cliënt, datum, tijd en duur.');
      return;
    }

    const selectedClient = users.find(u => u.id === newAppointmentForm.clientId);
    if (!selectedClient) {
        setNewAppointmentError('Geselecteerde cliënt niet gevonden.');
        return;
    }

    setNewAppointmentLoading(true);

    // --- Firestore Write Logic (Placeholder) ---
    console.log('Submitting new appointment:', newAppointmentForm);
    console.log('Selected client data:', selectedClient);

    // TODO: 
    // 1. Convert date string to Timestamp
    // 2. Check for conflicts with existing appointments/blocks
    // 3. Construct the Firestore document data (match Appointment interface)
    //    - userId, userEmail, userName (from selectedClient)
    //    - appointmentDate (Timestamp)
    //    - appointmentTime (string)
    //    - appointmentType (string)
    //    - duration (number in minutes - optional, or adjust interface)
    //    - status: 'confirmed'
    //    - requestedAt: serverTimestamp()
    //    - emailReminderScheduled: false (for backend function)
    //    - reminderDate: Timestamp (calculated 3 days before)
    // 4. Use addDoc to add to 'appointments' collection
    // 5. Handle success/error

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // --- END Firestore Placeholder ---
    
    setNewAppointmentLoading(false);
    // On success:
    // handleCloseNewAppointmentDialog();
    // fetchAppointments(); // Re-fetch to update lists
    // alert('Afspraak succesvol ingepland!');
    
    // On error:
    // setNewAppointmentError('Fout bij opslaan afspraak.');

     alert('Afspraak opslaan (DEMO - check console).'); // Temporary feedback
     handleCloseNewAppointmentDialog(); // Close for now

  };

  // --- NEW Handlers for Schedule Dialog ---
  const handleOpenScheduleDialog = (appointment: Appointment) => {
    setSchedulingAppointment(appointment);
    // Pre-fill date if possible, default time
    setScheduleForm({ date: format(new Date(), 'yyyy-MM-dd'), time: '14:00' }); 
    setScheduleError('');
    setScheduleLoading(false);
    setScheduleDialogOpen(true);
  };

  const handleCloseScheduleDialog = () => {
    setScheduleDialogOpen(false);
    setSchedulingAppointment(null);
  };

  const handleConfirmSchedule = async () => {
    if (!schedulingAppointment || !scheduleForm.date || !scheduleForm.time) {
      setScheduleError('Selecteer een geldige datum en tijd.');
      return;
    }
    setScheduleError('');
    setScheduleLoading(true);

    try {
      const appointmentRef = doc(db, "appointments", schedulingAppointment.id);
      
      // Convert date string to Timestamp
      let appointmentDateTimestamp: Timestamp | null = null;
      try {
        const parsedDate = new Date(scheduleForm.date + 'T' + scheduleForm.time + ':00'); // Combine date and time
        if (!isNaN(parsedDate.getTime())) {
          appointmentDateTimestamp = Timestamp.fromDate(parsedDate);
        } else {
          throw new Error('Invalid date/time combination');
        }
      } catch (dateError) {
         console.error("Error creating Timestamp:", dateError);
         setScheduleError('Ongeldige datum of tijd formaat.');
         setScheduleLoading(false);
         return;
      }

      await updateDoc(appointmentRef, { 
        status: 'confirmed',
        appointmentDate: appointmentDateTimestamp, // Save as Timestamp
        appointmentTime: scheduleForm.time // Save time as string
      });
      
      // Update local state
      setAppointments(prev => 
        prev.map(app => 
          app.id === schedulingAppointment.id 
            ? { 
                ...app, 
                status: 'confirmed', 
                // Ensure type compatibility: use undefined if timestamp is null
                appointmentDate: appointmentDateTimestamp ?? undefined, 
                appointmentTime: scheduleForm.time 
              } 
            : app
        )
      );

      console.log(`Appointment ${schedulingAppointment.id} confirmed for ${scheduleForm.date} at ${scheduleForm.time}`);
      alert('Afspraak succesvol bevestigd en ingepland!');
      handleCloseScheduleDialog();

    } catch (error) {
      console.error("Error confirming appointment schedule: ", error);
      setScheduleError("Kon afspraak niet bevestigen. Probeer het opnieuw.");
    } finally {
      setScheduleLoading(false);
    }
  };
  // --- END NEW Handlers ---

  // Adjust handleUpdateAppointmentStatus to ONLY handle rejection now
  const handleRejectAppointment = async (appointmentId: string) => {
    setUsersLoading(true); // Reuse existing loading state for simplicity
    try {
      const appointmentRef = doc(db, "appointments", appointmentId);
      await updateDoc(appointmentRef, { status: 'rejected' });
      // Refresh local state
      setAppointments(prev => prev.filter(app => app.id !== appointmentId)); // Remove rejected from view
      alert(`Afspraakverzoek afgewezen.`);
    } catch (error) {
      console.error("Error rejecting appointment status: ", error);
      alert("Kon status niet bijwerken. Probeer het opnieuw.");
    } finally {
      setUsersLoading(false);
    }
  };

  // --- Render Logic ---
  
  // Adjust main loading check if needed, or keep as is if initial user load is primary
  if (authLoading || (isCoach() && usersLoading)) { // Simplified check: wait for auth and initial user fetch
     return (
       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 200px)' }}>
         <CircularProgress />
         <Typography sx={{ ml: 2 }}>Dashboard laden...</Typography>
       </Box>
     );
   }
   
   // If user is confirmed not to be a coach (should have been redirected, but as fallback)
   if (!authLoading && !isCoach()) {
     return (
       <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
         <Alert severity="error">Geen toegang. U moet een coach zijn om dit dashboard te bekijken.</Alert>
       </Container>
     );
   }

  // Main dashboard render
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ 
            position: 'fixed', 
            top: 20, 
            right: 20, 
            zIndex: 9999,
            boxShadow: 3,
            maxWidth: '80%'
          }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Coach Dashboard
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="coach dashboard tabs" variant="scrollable">
            <Tab label="Agenda" icon={<CalendarIcon />} iconPosition="start" {...a11yProps(0)} />
            <Tab 
              label={`Afspraakverzoeken (${appointments.filter(a => ['pending', 'pending_user_request'].includes(a.status)).length})`} 
              icon={<EventIcon />} 
              iconPosition="start" 
              {...a11yProps(1)} 
              sx={{ 
                color: appointments.filter(a => ['pending', 'pending_user_request'].includes(a.status)).length > 0 ? 'error.main' : 'inherit',
                '&.Mui-selected': {
                  color: appointments.filter(a => ['pending', 'pending_user_request'].includes(a.status)).length > 0 ? 'error.main' : 'primary.main',
                }
              }}
            />
            <Tab label="Cliënten" icon={<PeopleIcon />} iconPosition="start" {...a11yProps(2)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" component="h2">
                    Agendaoverzicht
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    startIcon={<PersonAddIcon />} 
                    onClick={handleOpenNewAppointmentDialog} 
                    sx={{ mr: 1 }} // Add some margin if needed
                  >
                    Nieuwe Afspraak
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<RejectIcon />} 
                    onClick={handleOpenBlockDialog}
                  >
                    Tijd Blokkeren
                  </Button>
                </Box>
                
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    value={selectedDate}
                    onChange={handleDateChange}
                  />
                </LocalizationProvider>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, height: '100%' }}>
                {selectedDate && (
                  <>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {format(selectedDate, 'EEEE d MMMM yyyy', { locale: nl })}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="h6" gutterBottom>
                      Geblokkeerde tijden
                    </Typography>
                    
                    {blockedTimeSlots.filter(slot => slot.date === format(selectedDate, 'yyyy-MM-dd') && slot.startTime === '00:00' && slot.endTime === '23:59').length > 0 ? (
                       <Alert severity="warning" sx={{ mb: 2 }} action={
                         <IconButton size="small" onClick={() => handleRemoveBlockedTimeSlot(format(selectedDate, 'yyyy-MM-dd'))}>
                           <DeleteIcon fontSize="inherit" />
                         </IconButton>
                       }>
                         Hele dag geblokkeerd: {blockedTimeSlots.find(slot => slot.date === format(selectedDate, 'yyyy-MM-dd'))?.reason || '-'}
                       </Alert>
                     ) : (
                      blockedTimeSlots.filter(slot => slot.date === format(selectedDate, 'yyyy-MM-dd')).length > 0 ? (
                        <List dense>
                          {blockedTimeSlots
                            .filter(slot => slot.date === format(selectedDate, 'yyyy-MM-dd'))
                            .map(slot => (
                              <ListItem key={slot.id} divider>
                                <ListItemText 
                                  primary={`${slot.startTime} - ${slot.endTime}`} 
                                  secondary={slot.reason} 
                                />
                                <ListItemSecondaryAction>
                                  <IconButton edge="end" onClick={() => handleRemoveBlockedTimeSlot(slot.date)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                        </List>
                      ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Geen geblokkeerde tijden voor deze dag.
                        </Alert>
                      )
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="h6" gutterBottom>
                      Bevestigde Afspraken
                    </Typography>
                    
                    {getAppointmentsForDate(format(selectedDate, 'yyyy-MM-dd')).filter(a => a.status === 'confirmed').length > 0 ? (
                      <List dense>
                        {getAppointmentsForDate(format(selectedDate, 'yyyy-MM-dd'))
                          .filter(a => a.status === 'confirmed')
                          // Add fallback for potentially undefined appointmentTime
                          .sort((a, b) => (a.appointmentTime ?? '').localeCompare(b.appointmentTime ?? ''))
                          .map(appointment => (
                            <ListItem 
                              key={appointment.id} 
                              divider
                              secondaryAction={
                                  <IconButton edge="end" size="small" onClick={() => handleViewAppointment(appointment)} title="Details">
                                      <ViewIcon />
                                  </IconButton>
                              }
                            >
                              <ListItemText 
                                primary={`${appointment.appointmentTime} - ${appointment.userName}`} 
                                secondary={appointment.appointmentType} 
                              />
                            </ListItem>
                          ))}
                      </List>
                    ) : (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Geen bevestigde afspraken voor deze dag.
                      </Alert>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>Nieuwe afspraakverzoeken</Typography>
          {appointmentsLoading ? (
            <CircularProgress />
          ) : appointmentsError ? (
            <Alert severity="error">{appointmentsError}</Alert>
          ) : appointments.filter(a => ['pending', 'pending_user_request'].includes(a.status)).length === 0 ? (
            <Alert severity="info">Geen nieuwe afspraakverzoeken.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Aangevraagd Op</TableCell>
                  <TableCell>Naam</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Notities</TableCell>
                  <TableCell>Acties</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments
                  .filter(a => ['pending', 'pending_user_request'].includes(a.status))
                  .sort((a, b) => a.requestedAt.toMillis() - b.requestedAt.toMillis())
                  .map((appointment) => (
                    <TableRow key={appointment.id}>
                      <TableCell>{formatFirestoreTimestamp(appointment.requestedAt, 'dd/MM/yy HH:mm')}</TableCell>
                      <TableCell>{appointment.userName || 'N/A'}</TableCell>
                      <TableCell>{appointment.userEmail || 'N/A'}</TableCell>
                      <TableCell>{appointment.appointmentType}</TableCell>
                      <TableCell>
                        {appointment.notes && appointment.notes.length > 50 ? (
                          <Tooltip title={appointment.notes}>
                            <span>{appointment.notes.substring(0, 50)}...</span>
                          </Tooltip>
                        ) : (
                          appointment.notes || '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Plan in / Bevestig">
                          <IconButton 
                            size="small" 
                            color="success" 
                            onClick={() => handleOpenScheduleDialog(appointment)} 
                          >
                            <ConfirmIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Afwijzen">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleRejectAppointment(appointment.id)}
                          >
                            <RejectIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Gebruikersbeheer
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Beheer gebruikers en hun premium toegang. Stel hun premium status in en de einddatum van het premium abonnement.
            </Typography>

            {usersLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            )}
            
            {usersError && (
              <Alert severity="error" sx={{ mb: 3 }}>{usersError}</Alert>
            )}

            {!usersLoading && !usersError && (
              <Paper sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 650 }} aria-label="gebruikers tabel">
                  <TableHead>
                    <TableRow>
                      <TableCell>Naam</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Laatste Login</TableCell>
                      <TableCell align="center">Premium Actief?</TableCell>
                      <TableCell>Premium Vervalt Op</TableCell>
                      <TableCell>Volgende Afspraak</TableCell>
                      <TableCell align="right">Acties</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow
                        key={user.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          {user.name}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.lastLogin}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                mr: 1,
                                bgcolor: 
                                  user.subscriptionTier ? 
                                    TIER_CONFIG[user.subscriptionTier.toLowerCase() as keyof typeof TIER_CONFIG]?.color + '.main' :
                                  user.tier === 'Premium' ? 'primary.main' :
                                  user.tier === 'Complete' ? 'success.main' :
                                  user.tier === 'Self' ? 'info.main' :
                                  'grey.400'
                              }} 
                            />
                            <Typography variant="body2">
                              {user.subscriptionTier ? 
                                getTierNameByKey(user.subscriptionTier) : 
                                (user.tier === 'Premium' ? 'Premium' : 
                                 user.tier === 'Complete' ? 'Complete' :
                                 user.tier === 'Self' ? 'Self' :
                                 (language === 'nl' ? 'Geen' : 'None'))}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{user.premiumExpiresAt || '-'}</TableCell>
                        <TableCell>{user.nextAppointmentDate || '-'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Beheer Premium">
                            <IconButton size="small" onClick={() => handleOpenUserDialog(user)}>
                              <PremiumIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Paper>
        </TabPanel>
        
        <Dialog open={blockDialog} onClose={handleCloseBlockDialog}>
          <DialogTitle>Datum Bereik Blokkeren</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="block-start-date"
              label="Startdatum"
              type="date"
              fullWidth
              value={blockForm.startDate}
              onChange={(e) => setBlockForm({ ...blockForm, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="block-end-date"
              label="Einddatum (inclusief)"
              type="date"
              fullWidth
              value={blockForm.endDate}
              onChange={(e) => setBlockForm({ ...blockForm, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="block-reason"
              label="Reden (optioneel)"
              type="text"
              fullWidth
              value={blockForm.reason}
              onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBlockDialog}>Annuleren</Button>
            <Button onClick={() => handleAddBlockedTimeSlot('range')} variant="contained">Bereik Blokkeren</Button>
          </DialogActions>
        </Dialog>
        
        <Dialog open={viewAppointmentDialog} onClose={handleCloseViewAppointmentDialog}>
          <DialogTitle>Afspraak Details</DialogTitle>
          {selectedAppointment && (
            <DialogContent dividers>
               <Grid container spacing={2}>
                   <Grid item xs={6}><Typography variant="subtitle2">Klant:</Typography><Typography gutterBottom>{selectedAppointment.userName} ({selectedAppointment.userEmail})</Typography></Grid>
                   <Grid item xs={6}><Typography variant="subtitle2">Status:</Typography><Chip size="small" label={getStatusLabel(selectedAppointment.status)} color={getStatusColor(selectedAppointment.status)} /></Grid>
                   <Grid item xs={6}><Typography variant="subtitle2">Aangevraagd op:</Typography><Typography gutterBottom>{formatFirestoreTimestamp(selectedAppointment.requestedAt)}</Typography></Grid>
                   <Grid item xs={6}><Typography variant="subtitle2">Verzoek voor:</Typography><Typography gutterBottom>{formatFirestoreTimestamp(selectedAppointment.appointmentDate, 'EEEE dd/MM/yyyy')} om {selectedAppointment.appointmentTime}</Typography></Grid>
                   <Grid item xs={12}><Typography variant="subtitle2">Type:</Typography><Typography gutterBottom>{selectedAppointment.appointmentType}</Typography></Grid>
                   <Grid item xs={12}><Typography variant="subtitle2">Opmerkingen Klant:</Typography><Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedAppointment.notes || 'Geen'}</Typography></Grid>
                </Grid>
            </DialogContent>
          )}
          <DialogActions>
            <Button onClick={handleCloseViewAppointmentDialog}>Sluiten</Button>
            {selectedAppointment?.status === 'pending' && (
                   <> 
                       <Button 
                           onClick={() => handleUpdateAppointmentStatus(selectedAppointment.id, 'rejected')}
                           color="error"
                           startIcon={<RejectIcon />}
                           disabled={usersLoading}
                       >
                           Afwijzen
                       </Button>
                       <Button 
                           onClick={() => handleUpdateAppointmentStatus(selectedAppointment.id, 'confirmed')}
                           color="success"
                           variant="contained"
                           startIcon={<ConfirmIcon />}
                           disabled={usersLoading}
                       >
                           Bevestigen
                       </Button>
                   </>
              )}
          </DialogActions>
        </Dialog>

        {selectedUser && (
          <Dialog open={userDialog} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Gebruiker Beheren: {selectedUser.name}</DialogTitle>
            <DialogContent>
              {selectedUser && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1">Email: {selectedUser.email}</Typography>
                  <Typography variant="body1">Geregistreerd op: {selectedUser.registeredAt}</Typography>
                  <Typography variant="body1">Laatste login: {selectedUser.lastLogin}</Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Abonnement Beheren</Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {language === 'nl' ? 'Abonnement Status' : 'Subscription Status'}
                    </Typography>
                    
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: selectedUser?.subscriptionTier ? 
                          TIER_CONFIG[selectedUser.subscriptionTier.toLowerCase() as keyof typeof TIER_CONFIG]?.bgColor : 
                          'transparent'
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          mr: 2,
                          bgcolor: selectedUser?.subscriptionTier ? 
                            TIER_CONFIG[selectedUser.subscriptionTier.toLowerCase() as keyof typeof TIER_CONFIG]?.color + '.main' : 
                            'grey.400'
                        }} 
                      />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedUser?.subscriptionTier ? 
                          getTierNameByKey(selectedUser.subscriptionTier) : 
                          (language === 'nl' ? 'Geen Abonnement' : 'No Subscription')}
                      </Typography>
                      
                      {selectedUser?.subscriptionExpiry && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                          {language === 'nl' ? 'Verloopt op' : 'Expires on'}: {new Date(selectedUser.subscriptionExpiry).toLocaleDateString()}
                        </Typography>
                      )}
                    </Paper>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="tier-select-label">{language === 'nl' ? 'Abonnement Niveau' : 'Subscription Tier'}</InputLabel>
                        <Select
                          labelId="tier-select-label"
                          id="tier-select"
                          value={premiumForm.tier}
                          label={language === 'nl' ? 'Abonnement Niveau' : 'Subscription Tier'}
                          onChange={(e) => setPremiumForm({...premiumForm, tier: e.target.value as Tier})}
                        >
                          <MenuItem value={SUBSCRIPTION_TIERS.NONE}>
                            {language === 'nl' ? 'Geen' : 'None'}
                          </MenuItem>
                          <MenuItem value={SUBSCRIPTION_TIERS.ESSENTIAL}>
                            Essential
                          </MenuItem>
                          <MenuItem value={SUBSCRIPTION_TIERS.GOLD}>
                            Gold
                          </MenuItem>
                          <MenuItem value={SUBSCRIPTION_TIERS.ELITE}>
                            Elite
                          </MenuItem>
                          <MenuItem value={SUBSCRIPTION_TIERS.PLATINUM}>
                            Platinum
                          </MenuItem>
                          <MenuItem value="Self">
                            {language === 'nl' ? 'Zelfstudie' : 'Self-study'}
                          </MenuItem>
                          <MenuItem value="Premium">
                            Premium
                          </MenuItem>
                          <MenuItem value="Complete">
                            Complete
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        margin="normal"
                        fullWidth
                        id="expiry-date"
                        label={language === 'nl' ? 'Vervaldatum' : 'Expiry Date'}
                        type="date"
                        value={premiumForm.expiresAt}
                        onChange={(e) => setPremiumForm({...premiumForm, expiresAt: e.target.value})}
                        InputLabelProps={{ shrink: true }}
                        disabled={premiumForm.tier === SUBSCRIPTION_TIERS.NONE}
                        helperText={premiumForm.tier === SUBSCRIPTION_TIERS.NONE 
                          ? (language === 'nl' ? 'Geen vervaldatum nodig' : 'No expiry date needed') 
                          : ''}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseUserDialog}>Annuleren</Button>
              <Button onClick={handleUpdateUserPremium} variant="contained" startIcon={<CheckIcon />}>
                Opslaan
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <Dialog open={newAppointmentDialog} onClose={handleCloseNewAppointmentDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Nieuwe Afspraak Inplannen</DialogTitle>
          <DialogContent>
            <Box component="form" noValidate sx={{ mt: 1 }}>
              <TextField
                select
                required
                fullWidth
                margin="normal"
                id="new-appointment-client"
                label="Cliënt"
                value={newAppointmentForm.clientId}
                onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, clientId: e.target.value })}
                disabled={usersLoading || newAppointmentLoading}
              >
                <MenuItem value="" disabled>-- Selecteer Cliënt --</MenuItem>
                {users.filter(u => u.role !== 'coach' && u.role !== 'admin').map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                required
                fullWidth
                margin="normal"
                id="new-appointment-date"
                label="Datum"
                type="date"
                value={newAppointmentForm.date}
                onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={newAppointmentLoading}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    select
                    required
                    fullWidth
                    margin="normal"
                    id="new-appointment-time"
                    label="Starttijd"
                    value={newAppointmentForm.time}
                    onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, time: e.target.value })}
                    disabled={newAppointmentLoading}
                  >
                    {availableTimeSlots.map((timeSlot) => (
                      <MenuItem key={timeSlot} value={timeSlot}>
                        {timeSlot}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    required
                    fullWidth
                    margin="normal"
                    id="new-appointment-duration"
                    label="Duur"
                    value={newAppointmentForm.duration}
                    onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, duration: e.target.value })}
                    disabled={newAppointmentLoading}
                  >
                    <MenuItem value="30">0.5 uur</MenuItem>
                    <MenuItem value="60">1 uur</MenuItem>
                    <MenuItem value="90">1.5 uur</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

               <TextField
                 fullWidth
                 margin="normal"
                 id="new-appointment-type"
                 label="Type Afspraak (bijv. Intake, Vervolg)"
                 value={newAppointmentForm.type}
                 onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, type: e.target.value })}
                 disabled={newAppointmentLoading}
               />

                <TextField
                  fullWidth
                  margin="normal"
                  id="new-appointment-notes"
                  label="Notities (intern)"
                  multiline
                  rows={3}
                  value={newAppointmentForm.notes}
                  onChange={(e) => setNewAppointmentForm({ ...newAppointmentForm, notes: e.target.value })}
                  disabled={newAppointmentLoading}
                />

                {newAppointmentError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{newAppointmentError}</Alert>
                )}

            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNewAppointmentDialog} disabled={newAppointmentLoading}>Annuleren</Button>
            <Button onClick={handleNewAppointmentSubmit} variant="contained" disabled={newAppointmentLoading}>
              {newAppointmentLoading ? <CircularProgress size={24} /> : 'Afspraak Opslaan'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* --- NEW Schedule Appointment Dialog --- */} 
        <Dialog open={scheduleDialogOpen} onClose={handleCloseScheduleDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Afspraak Inplannen</DialogTitle>
          <DialogContent>
            {schedulingAppointment && (
              <Box sx={{ mt: 1 }}>
                 <Typography variant="subtitle1">Verzoek van:</Typography>
                 <Typography gutterBottom>{schedulingAppointment.userName} ({schedulingAppointment.userEmail})</Typography>
                 <Typography variant="subtitle1" sx={{ mt: 1 }}>Type:</Typography>
                 <Typography gutterBottom>{schedulingAppointment.appointmentType}</Typography>
                 {schedulingAppointment.notes && (
                  <>
                     <Typography variant="subtitle1" sx={{ mt: 1 }}>Opmerkingen:</Typography>
                     <Typography gutterBottom sx={{ whiteSpace: 'pre-wrap' }}>{schedulingAppointment.notes}</Typography>
                  </>
                 )}
                 <Divider sx={{ my: 2 }}/>
                 <Typography variant="subtitle1" gutterBottom>Selecteer Datum en Tijd:</Typography>
                 <TextField
                    required
                    fullWidth
                    margin="dense"
                    id="schedule-date"
                    label="Datum"
                    type="date"
                    value={scheduleForm.date}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    disabled={scheduleLoading}
                    error={!!scheduleError}
                  />
                  <TextField
                    select
                    required
                    fullWidth
                    margin="dense"
                    id="schedule-time"
                    label="Starttijd"
                    value={scheduleForm.time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                    disabled={scheduleLoading}
                    error={!!scheduleError}
                    helperText={scheduleError || ''}
                  >
                    {availableTimeSlots.map((timeSlot) => (
                      <MenuItem key={timeSlot} value={timeSlot}>
                        {timeSlot}
                      </MenuItem>
                    ))}
                  </TextField>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseScheduleDialog} disabled={scheduleLoading}>Annuleren</Button>
            <Button onClick={handleConfirmSchedule} variant="contained" disabled={scheduleLoading}>
               {scheduleLoading ? <CircularProgress size={24} /> : 'Bevestig Afspraak'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* --- END NEW Dialog --- */} 

        {selectedUser && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              {language === 'nl' ? 'Gebruiker beheren' : 'Manage User'}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <UserSubscriptionManager 
                  userId={selectedUser.id} 
                  currentTier={selectedUser.subscriptionTier || 
                              (selectedUser.tier !== 'None' ? selectedUser.tier.toLowerCase() : SUBSCRIPTION_TIERS.NONE)}
                  onUpdateSuccess={() => {
                    // Refresh user data after update
                    fetchAndProcessUsers([]);
                    // Also refresh the user's subscription to ensure current state is displayed
                    if (currentUser && currentUser.uid === selectedUser.id) {
                      refreshUserSubscription();
                    }
                    setMessage({
                      text: language === 'nl' ? 'Gebruiker bijgewerkt' : 'User updated successfully',
                      type: 'success'
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    {language === 'nl' ? 'GEBRUIKERSINFORMATIE' : 'USER INFORMATION'}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {language === 'nl' ? 'Email' : 'Email'}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedUser.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {language === 'nl' ? 'Naam' : 'Name'}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedUser.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {language === 'nl' ? 'Registratiedatum' : 'Registration Date'}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedUser.registeredAt || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {language === 'nl' ? 'Laatste login' : 'Last Login'}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedUser.lastLogin || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {language === 'nl' ? 'Rol' : 'Role'}
                      </Typography>
                      <Typography variant="body1" gutterBottom sx={{ textTransform: 'capitalize' }}>
                        {selectedUser.role || 'user'}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {selectedUser.nextAppointmentDate && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="primary.contrastText">
                        {language === 'nl' ? 'Volgende Afspraak' : 'Next Appointment'}
                      </Typography>
                      <Typography variant="body1" color="primary.contrastText">
                        {selectedUser.nextAppointmentDate}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

      </Container>
    </LocalizationProvider>
  );
};

export default CoachDashboard; 